import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import Sheet from './common/sheet.jsx';
import useAuth from '../../context/auth.context.jsx';
import userService from '../services/userService';
import { publicPath, publicLang } from '../pages/public/publicPaths';
import { acceptStampOk, needsLegalConsent } from '../utils/legalConsent.js';
import { legalDocUrl, openExternal } from '../utils/openExternal.js';

/**
 * Blocking consent gate: shown when the server says this account still needs
 * to accept the current terms (`needsLegalConsent` on getMe). Not dismissible
 * on purpose — this is the one dialog the user cannot click past.
 */
export default function LegalConsentGate({ children }) {
    const { userData, loadUserData, logOut } = useAuth();
    const { t, i18n } = useTranslation('auth');
    const [busy, setBusy] = useState(false);

    const user = userData?.user;
    const needsConsent = needsLegalConsent(user, userData);

    const accept = async () => {
        setBusy(true);
        try {
            const data = await userService.updateMe({ acceptLegal: true });
            if (!acceptStampOk(data)) {
                toast.error(t('consentGate.acceptFailed'));
                return;
            }
            await loadUserData();
        } catch {
            toast.error(t('consentGate.acceptFailed'));
        } finally {
            setBusy(false);
        }
    };

    if (!needsConsent) return children;

    const isFirstTime = user.legal?.acceptedVersion == null;
    const lang = publicLang(i18n.language);

    // Don't mount the app (or the tour overlay at z-index 1100) under this
    // sheet. The gate is non-dismissible; anything behind it is unreachable
    // on purpose, and a live tour would steal the accept click.
    return (
        <Sheet open title={isFirstTime ? t('consentGate.titleNew') : t('consentGate.titleUpdated')} dismissible={false}>
            <p className="text-muted-ms">
                {isFirstTime ? t('consentGate.bodyNew') : t('consentGate.bodyUpdated')}
            </p>
            <div className="d-flex gap-3 mb-3">
                <button type="button" className="btn-ms-link" onClick={() => openExternal(legalDocUrl(publicPath(lang, '/terms')))}>
                    {t('consentGate.termsLink')}
                </button>
                <button type="button" className="btn-ms-link" onClick={() => openExternal(legalDocUrl(publicPath(lang, '/privacy')))}>
                    {t('consentGate.privacyLink')}
                </button>
            </div>
            <button type="button" className="btn-ms btn-ms-primary btn-ms-block" onClick={accept} disabled={busy}>
                {busy ? t('consentGate.accepting') : t('consentGate.accept')}
            </button>
            <button type="button" className="btn-ms btn-ms-outline btn-ms-block mt-2" onClick={logOut}>
                {t('consentGate.logOut')}
            </button>
        </Sheet>
    );
}
